"use client";

import { useRef } from "react";
import { Bot, Crosshair, Lock } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { ROLE_COLORS, WORKSPACES } from "./data";

interface FeatureRow {
  icon: LucideIcon;
  title: string;
  body: string;
  pills?: readonly string[];
}

const FEATURES: FeatureRow[] = [
  {
    icon: Lock,
    title: "SSO — SAML on request",
    body: "Single sign-on for your identity provider on Enterprise, set up with our team — no shared logins.",
  },
  {
    icon: Bot,
    title: "Revocable agent access",
    body: "Every AI assistant connected over MCP with OAuth is listed in your settings, scoped to one workspace — disconnect it in one click.",
  },
  {
    icon: Crosshair,
    title: "Role-based access, per workspace",
    body: "Give an agency its own workspace and it sees nothing else. Owner, Admin, Editor, Viewer — set per workspace.",
  },
];

/** Pill styling for a member role — fg = role color, bg 9% on white, border 30%. */
const rolePill = (role: string) => {
  const color = ROLE_COLORS[role.split(" ")[0]] ?? "var(--color-muted)";
  return {
    color,
    background: `color-mix(in srgb, ${color} 9%, white)`,
    borderColor: `color-mix(in srgb, ${color} 30%, transparent)`,
  };
};

/** Governance — SSO (on request) / agent access / RBAC rows beside the acme org-tree card. */
export default function GovernanceSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-gov-row]", { y: 18 });
  useEnterStagger(rootRef, "[data-gov-card]", { y: 26 });

  return (
    <SectionShell id="governance" className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px">
      <div ref={rootRef}>
        <SectionHead
          eyebrow="Governance"
          title="One control plane, every team."
          lead="SSO / SAML on request, every team in its own workspace, and exactly who can create, edit or only view — set per workspace."
        />
        <div
          data-grid-block
          className="grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_480px] lg:gap-16"
        >
          {/* Left — the three governance capabilities. */}
          <div className="flex flex-col gap-[22px]">
            {FEATURES.map((f) => (
              <div key={f.title} data-gov-row className="flex items-start gap-4">
                <span className="grid h-9 w-9 flex-none place-items-center rounded-[10px] border border-brand-600/[0.18] bg-brand-600/[0.07] text-brand-600">
                  <f.icon size={17} strokeWidth={1.8} />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="text-[14.5px] font-bold">{f.title}</span>
                    {f.pills?.map((pill) => (
                      <span
                        key={pill}
                        className="rounded-full border border-line-2 bg-surface px-2.5 py-[3px] font-jetbrains text-[11px]"
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                  <p className="mt-1.5 text-[13.5px] leading-[1.55] text-muted">{f.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Right — the org-tree card. */}
          <div
            data-gov-card
            className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-ref-lg"
          >
            <div className="flex items-center gap-2.5 border-b border-line bg-surface-2 px-5 py-3.5">
              <span className="font-jetbrains text-xs font-semibold">acme</span>
              <span className="font-jetbrains text-[11px] text-dim">
                3 workspaces · 42 members · 4 roles
              </span>
            </div>
            <div className="flex flex-col gap-4 px-5 pb-[22px] pt-[18px]">
              {WORKSPACES.map((ws) => (
                <div key={ws.name}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[13.5px] font-bold">{ws.name}</span>
                    <span className="font-jetbrains text-[10.5px] text-dim">{ws.count}</span>
                  </div>
                  <div className="ml-[7px] mt-2 flex flex-col gap-[7px] border-l border-line-2 pl-3.5">
                    {ws.members.map((m) => (
                      <div key={m.who} className="flex items-center gap-2.5">
                        <span className="font-jetbrains text-xs text-muted">{m.who}</span>
                        <span
                          className="ml-auto whitespace-nowrap rounded-full border px-[9px] py-0.5 font-jetbrains text-[10px]"
                          style={rolePill(m.role)}
                        >
                          {m.role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="mt-1 font-jetbrains text-[10.5px] text-dim">
                roles scoped per workspace · uptime in writing{" "}
                <a href="#reliability" className="text-brand-600 underline-offset-2 hover:underline">
                  reliability ↓
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
